import React, { useState } from 'react'

interface Cliente {
  cliente_id: string
  nome_empresa: string
  segmento_nome: string | null
  faturamento: number | null
  roi: number | null
  leads: number | null
  vendas: number | null
  score_performance: number | null
  status_cliente: string
  tipo_oportunidade: string | null
  prioridade_real: number
  acao_recomendada: string
  potencial_receita: number | null
  crescimento_faturamento_pct: number | null
  nivel_dados: string
  dados_suspeitos: boolean
  tipo_inconsistencia: string | null
}

interface TabelaClientesProps {
  clientes: Cliente[]
  periodo?: string
  segmentoFiltro?: string
  statusFiltro?: string
}

const TabelaClientes: React.FC<TabelaClientesProps> = ({ 
  clientes, 
  periodo, 
  segmentoFiltro, 
  statusFiltro 
}) => {
  const [ordenacao, setOrdenacao] = useState<{
    campo: keyof Cliente
    direcao: 'asc' | 'desc'
  }>({
    campo: 'prioridade_real',
    direcao: 'desc'
  })

  const formatCurrency = (value: number | null) => {
    if (value === null || value === undefined) return 'N/A'
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value)
  }

  const getStatusBadge = (status: string) => {
    const badges: Record<string, { label: string; className: string; icon: string }> = {
      'risco_alto': { label: 'Risco Alto', className: 'bg-red-100 text-red-800 border-red-300', icon: '🚨' },
      'risco_medio': { label: 'Risco Médio', className: 'bg-orange-100 text-orange-800 border-orange-300', icon: '⚠️' },
      'saudavel': { label: 'Saudável', className: 'bg-green-100 text-green-800 border-green-300', icon: '✅' },
      'oportunidade': { label: 'Oportunidade', className: 'bg-blue-100 text-blue-800 border-blue-300', icon: '🎯' }
    }
    const badge = badges[status] || { label: status, className: 'bg-gray-100 text-gray-800 border-gray-300', icon: '📊' }
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded border ${badge.className}`}>
        <span>{badge.icon}</span>
        {badge.label}
      </span>
    )
  }

  const getTipoOportunidadeBadge = (tipo: string | null) => {
    if (!tipo) return <span className="text-gray-400 text-xs">-</span>
    
    const badges: Record<string, { label: string; className: string; icon: string }> = {
      'escala': { label: 'Escala', className: 'bg-purple-100 text-purple-800', icon: '📈' },
      'conversao': { label: 'Conversão', className: 'bg-blue-100 text-blue-800', icon: '🎯' },
      'maturidade': { label: 'Maturidade', className: 'bg-indigo-100 text-indigo-800', icon: '📚' },
      'estrutura': { label: 'Estrutura', className: 'bg-cyan-100 text-cyan-800', icon: '🏗️' }
    }
    const badge = badges[tipo] || { label: tipo, className: 'bg-gray-100 text-gray-800', icon: '📊' }
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded ${badge.className}`}>
        <span>{badge.icon}</span>
        {badge.label}
      </span>
    )
  }

  const getScoreColor = (score: number | null) => {
    if (score === null) return 'text-gray-400'
    if (score >= 75) return 'text-green-600 font-bold'
    if (score >= 60) return 'text-blue-600 font-semibold'
    if (score >= 40) return 'text-orange-600 font-semibold'
    return 'text-red-600 font-bold'
  }

  const getNivelDadosBadge = (nivel: string) => {
    const badges: Record<string, { label: string; className: string; icon: string }> = {
      'completo': { label: 'Completo', className: 'bg-green-100 text-green-800 border-green-300', icon: '✅' },
      'intermediario': { label: 'Intermediário', className: 'bg-yellow-100 text-yellow-800 border-yellow-300', icon: '⚠️' },
      'basico': { label: 'Básico', className: 'bg-orange-100 text-orange-800 border-orange-300', icon: '📊' },
      'insuficiente': { label: 'Insuficiente', className: 'bg-red-100 text-red-800 border-red-300', icon: '❌' }
    }
    const badge = badges[nivel] || { label: nivel, className: 'bg-gray-100 text-gray-800 border-gray-300', icon: '?' }
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded border ${badge.className}`}>
        <span>{badge.icon}</span>
        {badge.label}
      </span>
    )
  }

  const getInconsistenciaBadge = (tipo: string | null) => {
    if (!tipo) return null
    
    const badges: Record<string, { label: string; className: string }> = {
      'roi_extremo': { label: 'ROI Extremo', className: 'bg-orange-100 text-orange-800' },
      'faturamento_zero': { label: 'Fat. Zero', className: 'bg-red-100 text-red-800' },
      'investimento_zero': { label: 'Inv. Zero', className: 'bg-red-100 text-red-800' },
      'leads_zero': { label: 'Leads Zero', className: 'bg-red-100 text-red-800' },
      'vendas_zero': { label: 'Vendas Zero', className: 'bg-orange-100 text-orange-800' }
    }
    const badge = badges[tipo] || { label: tipo, className: 'bg-gray-100 text-gray-800' }
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded ${badge.className}`}>
        ⚠️ {badge.label}
      </span>
    )
  }

  const handleOrdenar = (campo: keyof Cliente) => {
    setOrdenacao(prev => ({
      campo,
      direcao: prev.campo === campo && prev.direcao === 'desc' ? 'asc' : 'desc'
    }))
  }

  const clientesOrdenados = [...clientes].sort((a, b) => {
    const valorA = a[ordenacao.campo]
    const valorB = b[ordenacao.campo]
    
    if (valorA === null) return 1
    if (valorB === null) return -1
    
    const comparacao = valorA < valorB ? -1 : valorA > valorB ? 1 : 0
    return ordenacao.direcao === 'asc' ? comparacao : -comparacao
  })

  const HeaderCell: React.FC<{ campo: keyof Cliente; label: string; sortable?: boolean }> = ({ 
    campo, 
    label, 
    sortable = true 
  }) => (
    <th
      className={`px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider ${
        sortable ? 'cursor-pointer hover:bg-gray-100' : ''
      }`}
      onClick={() => sortable && handleOrdenar(campo)}
    >
      <div className="flex items-center gap-2">
        {label}
        {sortable && ordenacao.campo === campo && (
          <span className="text-blue-600">
            {ordenacao.direcao === 'desc' ? '↓' : '↑'}
          </span>
        )}
      </div>
    </th>
  )

  return (
    <div className="bg-white rounded-xl shadow-lg overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-2xl">📊</span>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Visão Operacional por Cliente</h2>
              <p className="text-sm text-gray-600">{clientes.length} clientes com dados completos</p>
            </div>
          </div>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <HeaderCell campo="prioridade_real" label="Prior." />
              <HeaderCell campo="nome_empresa" label="Cliente" />
              <HeaderCell campo="segmento_nome" label="Segmento" sortable={false} />
              <HeaderCell campo="faturamento" label="Faturamento" />
              <HeaderCell campo="roi" label="ROI %" />
              <HeaderCell campo="leads" label="Leads" />
              <HeaderCell campo="vendas" label="Vendas" />
              <HeaderCell campo="score_performance" label="Score" />
              <HeaderCell campo="status_cliente" label="Status" sortable={false} />
              <HeaderCell campo="tipo_oportunidade" label="Oportunidade" sortable={false} />
              <HeaderCell campo="nivel_dados" label="Qualidade" sortable={false} />
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                Ação Recomendada
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {clientesOrdenados.map((cliente) => (
              <tr key={cliente.cliente_id} className="hover:bg-gray-50 transition-colors">
                <td className="px-4 py-3 whitespace-nowrap">
                  <div className="flex items-center justify-center">
                    <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold ${
                      cliente.prioridade_real >= 80 ? 'bg-red-100 text-red-700' :
                      cliente.prioridade_real >= 60 ? 'bg-orange-100 text-orange-700' :
                      cliente.prioridade_real >= 40 ? 'bg-yellow-100 text-yellow-700' :
                      'bg-green-100 text-green-700'
                    }`}>
                      {cliente.prioridade_real}
                    </span>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <div className="text-sm font-medium text-gray-900">{cliente.nome_empresa}</div>
                  {cliente.crescimento_faturamento_pct !== null && (
                    <div className={`text-xs ${cliente.crescimento_faturamento_pct >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {cliente.crescimento_faturamento_pct >= 0 ? '↑' : '↓'} {Math.abs(cliente.crescimento_faturamento_pct).toFixed(1)}%
                    </div>
                  )}
                </td>
                <td className="px-4 py-3 whitespace-nowrap">
                  <span className="text-sm text-gray-700">{cliente.segmento_nome || '-'}</span>
                </td>
                <td className="px-4 py-3 whitespace-nowrap">
                  <span className="text-sm font-semibold text-gray-900">{formatCurrency(cliente.faturamento)}</span>
                </td>
                <td className="px-4 py-3 whitespace-nowrap">
                  <span className="text-sm font-semibold text-gray-900">
                    {cliente.roi !== null ? `${cliente.roi.toFixed(0)}%` : 'N/A'}
                  </span>
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-center">
                  <span className="text-sm text-gray-700">{cliente.leads || 0}</span>
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-center">
                  <span className="text-sm text-gray-700">{cliente.vendas || 0}</span>
                </td>
                <td className="px-4 py-3 whitespace-nowrap">
                  <span className={`text-sm ${getScoreColor(cliente.score_performance)}`}>
                    {cliente.score_performance !== null ? cliente.score_performance.toFixed(0) : 'N/A'}
                  </span>
                </td>
                <td className="px-4 py-3 whitespace-nowrap">
                  {getStatusBadge(cliente.status_cliente)}
                </td>
                <td className="px-4 py-3 whitespace-nowrap">
                  {getTipoOportunidadeBadge(cliente.tipo_oportunidade)}
                </td>
                <td className="px-4 py-3 whitespace-nowrap">
                  <div className="flex flex-col gap-1">
                    {getNivelDadosBadge(cliente.nivel_dados)}
                    {cliente.dados_suspeitos && getInconsistenciaBadge(cliente.tipo_inconsistencia)}
                  </div>
                </td>
                <td className="px-4 py-3">
                  <div className="text-sm text-gray-700 max-w-xs">
                    {cliente.acao_recomendada}
                  </div>
                  {cliente.potencial_receita !== null && cliente.potencial_receita > 0 && (
                    <div className="text-xs text-green-600 mt-1">
                      💰 Potencial: {formatCurrency(cliente.potencial_receita)}
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {clientes.length === 0 && (
        <div className="text-center py-12 px-6">
          <div className="text-6xl mb-4">📭</div>
          <p className="text-gray-700 text-lg font-semibold mb-4">Nenhum cliente encontrado</p>
          
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 max-w-md mx-auto text-left">
            <p className="text-sm font-medium text-blue-900 mb-2">Filtros ativos:</p>
            <ul className="text-sm text-blue-800 space-y-1">
              {periodo && (
                <li>• <strong>Período:</strong> {new Date(periodo).toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' })}</li>
              )}
              {segmentoFiltro && segmentoFiltro !== 'todos' && (
                <li>• <strong>Segmento:</strong> {segmentoFiltro}</li>
              )}
              {statusFiltro && statusFiltro !== 'todos' && (
                <li>• <strong>Status:</strong> {statusFiltro}</li>
              )}
            </ul>
          </div>

          <div className="mt-6 text-sm text-gray-600">
            <p className="font-medium mb-2">Sugestões:</p>
            <ul className="space-y-1">
              <li>✓ Tente selecionar outro período</li>
              <li>✓ Remova os filtros de segmento ou status</li>
              <li>✓ Verifique se há dados cadastrados para este período</li>
            </ul>
          </div>
        </div>
      )}
    </div>
  )
}

export default TabelaClientes
